/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
// Third-party
import { css, Global } from '@emotion/react'
import {
  Modal,
  Form,
  Select,
  Input,
  Switch,
  InputNumber,
  DatePicker,
  message,
} from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
import { font, weightMedium, weightSemiBold } from 'src/components/css'
import TimePickerWithInterval from 'src/components/ts-components/TimePickerWithInterval'
// Relative
import useConstraintTypes from '../hooks/useConstraintTypes'
import {
  CreateConstraintData,
  SchedulerConstraint,
} from '../hooks/useSchedulerConstraints'
import { darkPortalStyles, selectAllItemStyles } from '../styles/shared'
import { mapCategoryToDisplay, getConstraintDisplayName } from '../constants'
import {
  FacilityPercentageTargetRow,
  facilityPercentageRowsToTargets,
  facilityPercentageTargetsToRows,
  validateFacilityPercentageRows,
} from '../utils/constraintFormUtils'
import {
  HardSoftToggle,
  DayPicker,
} from './DivisionManagement/ConstraintParameterFields'

const fullWidthStyle = { width: '100%' } as const

const { Option, OptGroup } = Select
const { TextArea } = Input

/**
 * Division type from parent component
 */
interface Division {
  uid: string
  name: string
}

/**
 * Team type from parent component
 */
interface Team {
  uid: string
  name: string
  divisionUid: string
}

/**
 * Venue type from parent component
 */
interface Venue {
  uid: string
  name: string
}

/**
 * Facility type from parent component
 */
interface Facility {
  uid: string
  name: string
}

/**
 * Data for updating a constraint
 */
export interface UpdateConstraintData {
  parameters?: Record<string, unknown>
  description?: string
  is_hard?: boolean
  is_enabled?: boolean
  division_uids?: string[] | null
  team_uids?: string[] | null
}

/**
 * Props for AddConstraintModal
 */
interface AddConstraintModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: CreateConstraintData) => Promise<void>
  onUpdate?: (constraintId: string, data: UpdateConstraintData) => Promise<void>
  divisions?: Division[]
  teams?: Team[]
  venues?: Venue[]
  facilities?: Facility[]
  existingConstraints?: SchedulerConstraint[]
  editingConstraint?: SchedulerConstraint | null
  preSelectedTypeCode?: string | null
  allowedTypeCodes?: string[]
}

// ============================================
// Styles
// ============================================

const modalStyles = css`
  .ant-modal-content {
    background-color: ${colors.SECONDARY_300};
    border-radius: 8px;
    padding: 0;
    overflow: hidden;
  }

  .ant-modal-body {
    padding: 0;
  }

  .ant-modal-header,
  .ant-modal-footer {
    display: none;
  }

  .ant-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-label > label {
    color: ${colors.WHITE};
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 500;
  }

  .ant-input,
  .ant-input-number,
  .ant-picker {
    background-color: ${colors.SECONDARY_200} !important;
    border-color: ${colors.SECONDARY_100} !important;
    color: ${colors.WHITE} !important;
    border-radius: 8px !important;
    min-height: 40px !important;
    box-shadow: none !important;
  }

  .ant-input:focus,
  .ant-input-focused,
  .ant-input-number:focus,
  .ant-input-number-focused,
  .ant-picker-focused {
    border-color: ${colors.SECONDARY_100} !important;
    box-shadow: none !important;
  }

  .ant-input-number-input {
    color: ${colors.WHITE} !important;
  }

  .ant-select-selector {
    background-color: ${colors.SECONDARY_200} !important;
    border-color: ${colors.SECONDARY_100} !important;
    color: ${colors.WHITE} !important;
    border-radius: 8px !important;
    min-height: 40px !important;
    box-shadow: none !important;
  }

  .ant-select-focused .ant-select-selector {
    border-color: ${colors.SECONDARY_100} !important;
    box-shadow: none !important;
  }

  .ant-select-arrow,
  .ant-picker-suffix {
    color: ${colors.NEUTRAL_100};
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    color: ${colors.WHITE};
  }

  .ant-checkbox-wrapper {
    color: ${colors.WHITE};
  }

  textarea.ant-input {
    background-color: ${colors.SECONDARY_200} !important;
    border-color: ${colors.SECONDARY_100} !important;
    color: ${colors.WHITE} !important;
    border-radius: 8px !important;
    resize: vertical;
    min-height: auto !important;
  }

  .ant-switch {
    background-color: ${colors.NEUTRAL_600};
  }

  .ant-switch-checked {
    background-color: ${colors.PRIMARY} !important;
  }

  .ant-input-number-group-addon {
    background-color: ${colors.SECONDARY_200} !important;
    border-color: ${colors.SECONDARY_100} !important;
    color: ${colors.NEUTRAL_50} !important;
  }

  .ant-picker-input > input {
    color: ${colors.WHITE} !important;
  }

  .ant-form-item-explain-error {
    font-size: 12px;
    margin-top: 4px;
  }
`

const headerStyles = css`
  padding: 16px;
  border-bottom: 1px solid ${colors.SECONDARY_100};
`

const headerContentStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const headerTitleStyles = css`
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 600;
  font-size: 28px;
  line-height: 33.6px;
  color: ${colors.WHITE};
  margin: 0;
`

const closeButtonStyles = css`
  background: transparent;
  border: none;
  color: ${colors.WHITE};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;

  &:hover {
    color: ${colors.NEUTRAL_50};
  }
`

const formBodyStyles = css`
  padding: 16px;
`

const typeActiveRowStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
`

const typeActiveSectionStyles = css`
  display: flex;
  flex: 1;
  gap: 8px;
  align-items: flex-start;
  min-width: 0;
`

const typeActiveInfoStyles = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const typeActiveLabelStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 23.2px;
  color: ${colors.WHITE};
`

const typeActiveDescStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 17.4px;
  color: ${colors.NEUTRAL_50};
`

const verticalDividerStyles = css`
  width: 1px;
  align-self: stretch;
  background-color: ${colors.SECONDARY_100};
`

const fieldContainerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const fieldLabelStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20.3px;
  color: ${colors.WHITE};
`

const RequiredMark: React.FC = () => (
  <span
    css={css`
      color: ${colors.ERROR_300};
      margin-left: 2px;
    `}
  >
    {' *'}
  </span>
)

const paramGroupStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

/* facility_distribution percentage-targets editor — one row per target:
   [Location/Surface] [entity] [percent] [remove] */
const percentageTargetRowStyles = css`
  display: flex;
  align-items: flex-start;
  gap: 8px;

  .ant-form-item {
    margin-bottom: 8px;
  }
`

const percentageLevelItemStyles = css`
  width: 110px;
  flex-shrink: 0;
`

const percentageEntityItemStyles = css`
  flex: 1;
  min-width: 0;
`

const percentagePercentItemStyles = css`
  width: 110px;
  flex-shrink: 0;
`

const percentageRemoveButtonStyles = css`
  background: none;
  border: none;
  cursor: pointer;
  color: ${colors.WHITE};
  padding: 8px 4px;
  flex-shrink: 0;

  &:hover {
    color: ${colors.ERROR_300};
  }
`

const percentageAddButtonStyles = css`
  background: none;
  border: 1px dashed ${colors.SECONDARY_100};
  border-radius: 4px;
  cursor: pointer;
  color: ${colors.WHITE};
  padding: 6px 12px;
  align-self: flex-start;
  ${font.body}
  font-size: 14px;

  &:hover {
    border-color: ${colors.PRIMARY};
  }
`

const footerStyles = css`
  padding: 16px;
  border-top: 1px solid ${colors.SECONDARY_100};
  display: flex;
  justify-content: flex-end;
`

const footerButtonsStyles = css`
  display: flex;
  gap: 12px;
`

const cancelButtonStyles = css`
  padding: 8px 24px;
  background: transparent;
  border: 1px solid ${colors.NEUTRAL_100};
  border-radius: 8px;
  color: ${colors.NEUTRAL_50};
  ${font.body}
  ${weightMedium}
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;

  &:hover {
    border-color: ${colors.WHITE};
    color: ${colors.WHITE};
  }
`

const confirmButtonStyles = css`
  padding: 8px 24px;
  background: ${colors.PRIMARY};
  border: none;
  border-radius: 8px;
  color: ${colors.PRIMARY_TEXT};
  ${font.body}
  ${weightSemiBold}
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;

  &:hover {
    background: ${colors.PRIMARY_HOVER};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

// HardSoftToggle and DayPicker are now imported from
// ./DivisionManagement/ConstraintParameterFields

// ============================================
// AddConstraintModal Component
// ============================================

export const AddConstraintModal: React.FC<AddConstraintModalProps> = ({
  open,
  onClose,
  onSubmit,
  onUpdate,
  divisions = [],
  teams = [],
  venues = [],
  facilities = [],
  existingConstraints = [],
  editingConstraint,
  preSelectedTypeCode = null,
  allowedTypeCodes,
}) => {
  const isCloneMode = editingConstraint?.id === '__clone__'
  const isEditMode = !!editingConstraint && !isCloneMode
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const [selectedTypeCode, setSelectedTypeCode] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const pendingFormValuesRef = useRef<Record<string, unknown> | null>(null)
  const { constraintTypes, loading: typesLoading } = useConstraintTypes()

  // Refs to close dropdowns after "Select All"
  const divisionsSelectRef = useRef<any>(null)
  const teamsSelectRef = useRef<any>(null)
  const facilitiesSelectRef = useRef<any>(null)
  const venuesSelectRef = useRef<any>(null)

  // Refs for values used in the init effect (prevent re-triggering on selection)
  const divisionsRef = useRef(divisions)
  divisionsRef.current = divisions
  const teamsRef = useRef(teams)
  teamsRef.current = teams

  const selectedType = constraintTypes.find((t) => t.code === selectedTypeCode)

  // Pre-filter by allowedTypeCodes when provided. Always exclude
  // pair_matchup_count — it has its own dedicated grid editor mounted
  // from the Constraints tab and shouldn't appear in this generic
  // add-constraint flow (the team-multi-pick + parameter form fits
  // poorly for an N×N matrix).
  const allowedConstraintTypes = useMemo(() => {
    const base = allowedTypeCodes
      ? constraintTypes.filter((t) => allowedTypeCodes.includes(t.code))
      : constraintTypes
    return base.filter((t) => t.code !== 'pair_matchup_count')
  }, [constraintTypes, allowedTypeCodes])

  // Get unique categories from constraint types
  const categories = useMemo(() => {
    const cats = new Map<string, string>()
    allowedConstraintTypes.forEach((t) => {
      cats.set(t.category, mapCategoryToDisplay(t.category))
    })
    return Array.from(cats.entries()).map(([value, label]) => ({
      value,
      label,
    }))
  }, [allowedConstraintTypes])

  // Filter constraint types by selected category
  const filteredConstraintTypes = useMemo(() => {
    if (!selectedCategory) {
      return allowedConstraintTypes
    }
    return allowedConstraintTypes.filter((t) => t.category === selectedCategory)
  }, [allowedConstraintTypes, selectedCategory])

  // Watch selected divisions to filter teams
  const selectedDivisionUids: string[] | undefined = Form.useWatch(
    'division_uids',
    form
  )

  // Watch start times for duration display in end time pickers
  const watchedStartTime: Dayjs | undefined = Form.useWatch('start_time', form)
  const watchedPrefStartTime: Dayjs | undefined = Form.useWatch(
    'pref_start_time',
    form
  )

  // Filter teams based on selected divisions
  const filteredTeams = useMemo(() => {
    if (!selectedDivisionUids || selectedDivisionUids.length === 0) {
      return teams
    }
    return teams.filter((t) => selectedDivisionUids.includes(t.divisionUid))
  }, [teams, selectedDivisionUids])

  // Group filtered teams by division for display
  const teamsByDivision = useMemo(() => {
    const grouped: { division: Division; teams: Team[] }[] = []
    const divisionMap = new Map<string, Team[]>()

    filteredTeams.forEach((team) => {
      const existing = divisionMap.get(team.divisionUid) || []
      existing.push(team)
      divisionMap.set(team.divisionUid, existing)
    })

    divisionMap.forEach((divisionTeams, divisionUid) => {
      const division = divisions.find((d) => d.uid === divisionUid)
      if (division) {
        grouped.push({ division, teams: divisionTeams })
      }
    })

    grouped.sort((a, b) => a.division.name.localeCompare(b.division.name))
    return grouped
  }, [filteredTeams, divisions])

  // Clear invalid team selections when divisions change
  useEffect(() => {
    if (!selectedDivisionUids || selectedDivisionUids.length === 0) {
      return
    }
    const currentTeamUids: string[] | undefined =
      form.getFieldValue('team_uids')
    if (currentTeamUids && currentTeamUids.length > 0) {
      const validTeamUids = currentTeamUids.filter((teamUid) =>
        filteredTeams.some((t) => t.uid === teamUid)
      )
      if (validTeamUids.length !== currentTeamUids.length) {
        form.setFieldsValue({ team_uids: validTeamUids })
      }
    }
  }, [selectedDivisionUids, filteredTeams, form])

  // Reset form when modal opens/closes or pre-populate in edit mode
  useEffect(() => {
    if (!open) {
      // Reset type so the deferred effect fires on next open
      setSelectedTypeCode(null)
      setSelectedCategory(null)
      return
    }
    form.resetFields()
    if (editingConstraint) {
      // Pre-populate form with existing values for edit mode
      setSelectedTypeCode(editingConstraint.type.code)
      setSelectedCategory((editingConstraint.type as any).category || null)
      const params = editingConstraint.parameters as Record<string, unknown>

      // Set always-mounted fields immediately
      const commonValues: Record<string, unknown> = {
        constraint_type_code: editingConstraint.type.code,
        description: editingConstraint.description || '',
        is_hard: editingConstraint.is_hard,
        is_enabled: editingConstraint.is_enabled,
      }
      form.setFieldsValue(commonValues)

      // Defer fields whose Form.Items mount after selectedTypeCode re-render
      // (scope selectors + type-specific parameter fields)
      // Use refs to avoid re-triggering this effect when divisions/teams change
      const paramValues: Record<string, unknown> = {
        division_uids: Array.isArray(editingConstraint.division_uids)
          ? editingConstraint.division_uids
          : [],
        team_uids: Array.isArray(editingConstraint.team_uids)
          ? editingConstraint.team_uids
          : [],
      }
      switch (editingConstraint.type.code) {
        case 'venue_blackout':
          paramValues.venue_uids =
            params.venue_uids || (params.venue_uid ? [params.venue_uid] : [])
          paramValues.facility_uids =
            params.facility_uids ||
            (params.facility_uid ? [params.facility_uid] : [])
          if (params.dates) {
            paramValues.dates = (params.dates as string[]).map((d: string) =>
              dayjs(d)
            )
          }
          break
        case 'no_back_to_back': {
          const existingMinutes = params.min_minutes_between as
            | number
            | undefined
          const existingHours = params.min_hours_between as number | undefined
          paramValues.min_minutes_between =
            existingMinutes ?? (existingHours ? existingHours * 60 : 60)
          break
        }
        case 'min_rest_days':
          paramValues.min_days = params.min_days || 1
          break
        case 'bye_distribution':
          paramValues.max_consecutive_byes = params.max_consecutive_byes || 2
          break
        case 'home_away_balance':
          paramValues.target_home_percentage =
            params.target_home_percentage || 50
          break
        case 'preferred_day':
          paramValues.preferred_days = params.days || []
          break
        case 'restricted_day':
          paramValues.restricted_days = params.days || []
          break
        case 'max_consecutive_late_games':
          if (params.late_time_threshold) {
            paramValues.late_time_threshold = dayjs(
              params.late_time_threshold as string,
              'HH:mm'
            )
          }
          paramValues.max_consecutive = params.max_consecutive || 2
          break
        case 'time_window':
          if (params.start_time) {
            paramValues.start_time = dayjs(params.start_time as string, 'HH:mm')
          }
          if (params.end_time) {
            paramValues.end_time = dayjs(params.end_time as string, 'HH:mm')
          }
          paramValues.days = params.days || []
          break
        case 'preferred_time':
          if (params.start_time) {
            paramValues.pref_start_time = dayjs(
              params.start_time as string,
              'HH:mm'
            )
          }
          if (params.end_time) {
            paramValues.pref_end_time = dayjs(
              params.end_time as string,
              'HH:mm'
            )
          }
          break
        case 'team_bye_period':
          if (params.start_date && params.end_date) {
            paramValues.bye_date_range = [
              dayjs(params.start_date as string),
              dayjs(params.end_date as string),
            ]
          }
          break
        case 'venue_capacity':
          paramValues.facility_uids =
            params.facility_uids ||
            (params.facility_uid ? [params.facility_uid] : [])
          paramValues.max_concurrent_games = params.max_concurrent_games
          break
        case 'max_games_per_team_per_day':
          paramValues.max_games = params.max_games || 2
          break
        case 'games_per_team_per_week':
          paramValues.games_per_week = params.games_per_week || 1
          break
        case 'facility_distribution': {
          // A saved row carries `targets` and nothing else. This overrides
          // the Form.List's one-blank-row initialValue, so editing shows the
          // real targets rather than an empty editor.
          paramValues.targets = facilityPercentageTargetsToRows(
            params.targets as
              | {
                  facility_uid?: string
                  venue_uid?: string
                  percent?: number
                }[]
              | undefined
          )
          break
        }
      }
      pendingFormValuesRef.current = paramValues
    } else if (preSelectedTypeCode) {
      // Pre-select type from placeholder row click
      const preType = constraintTypes.find(
        (t) => t.code === preSelectedTypeCode
      )
      if (preType) {
        setSelectedTypeCode(preType.code)
        setSelectedCategory(preType.category)
        form.setFieldsValue({
          constraint_type_code: preType.code,
          is_hard: preType.is_hard_default,
        })
      }
      pendingFormValuesRef.current = null
    } else {
      pendingFormValuesRef.current = null
      setSelectedTypeCode(null)
      setSelectedCategory(null)
    }
  }, [open, form, editingConstraint, preSelectedTypeCode, constraintTypes])

  // Apply deferred parameter values after type-specific Form.Items have mounted
  useEffect(() => {
    if (pendingFormValuesRef.current && selectedTypeCode) {
      const raf = requestAnimationFrame(() => {
        if (pendingFormValuesRef.current) {
          form.setFieldsValue(pendingFormValuesRef.current)
          pendingFormValuesRef.current = null
        }
      })
      return () => cancelAnimationFrame(raf)
    }
  }, [selectedTypeCode, form])

  // Set default is_hard based on selected type (only in add mode)
  useEffect(() => {
    if (selectedType && !editingConstraint) {
      form.setFieldsValue({ is_hard: selectedType.is_hard_default })
    }
  }, [selectedType, form, editingConstraint])

  const handleCategoryChange = useCallback(
    (value: string) => {
      setSelectedCategory(value)
      // Reset constraint type when category changes
      setSelectedTypeCode(null)
      form.setFieldsValue({ constraint_type_code: undefined })
    },
    [form]
  )

  const handleTypeChange = useCallback(
    (value: string) => {
      setSelectedTypeCode(value)
      // Auto-set category if not already set
      if (!selectedCategory) {
        const type = constraintTypes.find((t) => t.code === value)
        if (type) {
          setSelectedCategory(type.category)
        }
      }
    },
    [constraintTypes, selectedCategory]
  )

  // Handle "All Divisions" — always selects all
  // Close the active Select dropdown after selecting all
  const blurActiveSelect = useCallback(() => {
    setTimeout(() => (document.activeElement as HTMLElement)?.blur(), 0)
  }, [])

  const handleSelectAllDivisions = useCallback(() => {
    form.setFieldsValue({ division_uids: divisions.map((d) => d.uid) })
    blurActiveSelect()
  }, [divisions, form, blurActiveSelect])

  // Handle "All Teams" — always selects all
  const handleSelectAllTeams = useCallback(() => {
    form.setFieldsValue({ team_uids: filteredTeams.map((t) => t.uid) })
    blurActiveSelect()
  }, [filteredTeams, form, blurActiveSelect])

  // Handle "All Facilities" — always selects all
  const handleSelectAllFacilities = useCallback(() => {
    form.setFieldsValue({ facility_uids: facilities.map((f) => f.uid) })
    blurActiveSelect()
  }, [facilities, form, blurActiveSelect])

  // Handle "All Venues" — always selects all
  const handleSelectAllVenues = useCallback(() => {
    form.setFieldsValue({ venue_uids: venues.map((v) => v.uid) })
    blurActiveSelect()
  }, [venues, form, blurActiveSelect])

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields()
      setSubmitting(true)

      const effectiveTypeCode = selectedTypeCode || values.constraint_type_code
      // Scope selectors are hidden for venue_blackout / venue_capacity —
      // preserve the original constraint's scope when cloning or editing.
      const scopeFieldsHidden =
        effectiveTypeCode === 'venue_blackout' ||
        effectiveTypeCode === 'venue_capacity'
      const fallbackDivisionUids =
        scopeFieldsHidden && editingConstraint
          ? editingConstraint.division_uids ?? null
          : null
      const fallbackTeamUids =
        scopeFieldsHidden && editingConstraint
          ? editingConstraint.team_uids ?? null
          : null

      const constraintData: CreateConstraintData = {
        constraint_type_code: effectiveTypeCode,
        parameters: {},
        description: values.description || selectedType?.name || undefined,
        // venue_capacity is a physical limit — always hard (the HARD|SOFT toggle
        // is locked for it). Force it so the persisted value can never be soft.
        is_hard: effectiveTypeCode === 'venue_capacity' ? true : values.is_hard,
        is_enabled: values.is_enabled !== false,
        division_uids:
          values.division_uids?.length > 0
            ? values.division_uids
            : fallbackDivisionUids,
        team_uids:
          values.team_uids?.length > 0 ? values.team_uids : fallbackTeamUids,
      }

      // Build parameters based on constraint type
      switch (effectiveTypeCode) {
        case 'venue_blackout':
          constraintData.parameters = {
            dates: (() => {
              if (!values.dates || values.dates.length < 2) {
                return (
                  values.dates?.map((d: Dayjs) => d.format('YYYY-MM-DD')) || []
                )
              }
              const start = values.dates[0]
              const end = values.dates[1]
              const result: string[] = []
              let current = start.startOf('day')
              const endDay = end.startOf('day')
              while (
                current.isBefore(endDay) ||
                current.isSame(endDay, 'day')
              ) {
                result.push(current.format('YYYY-MM-DD'))
                current = current.add(1, 'day')
              }
              return result
            })(),
          }
          if (values.venue_uids?.length > 0) {
            constraintData.parameters.venue_uids = values.venue_uids
          }
          if (values.facility_uids?.length > 0) {
            constraintData.parameters.facility_uids = values.facility_uids
          }
          break

        case 'venue_capacity':
          constraintData.parameters = {
            facility_uids: values.facility_uids,
            max_concurrent_games: values.max_concurrent_games,
          }
          break

        case 'time_window':
          constraintData.parameters = {
            start_time: values.start_time?.format('HH:mm'),
            end_time: values.end_time?.format('HH:mm'),
          }
          if (values.days?.length > 0) {
            constraintData.parameters.days = values.days
          }
          break

        case 'team_bye_period':
          constraintData.parameters = {
            start_date: values.bye_date_range?.[0]?.format('YYYY-MM-DD'),
            end_date: values.bye_date_range?.[1]?.format('YYYY-MM-DD'),
          }
          break

        case 'no_back_to_back':
          constraintData.parameters = {
            min_minutes_between: values.min_minutes_between,
          }
          break

        case 'min_rest_days':
          constraintData.parameters = {
            min_days: values.min_days,
          }
          break

        case 'bye_distribution':
          constraintData.parameters = {
            max_consecutive_byes: values.max_consecutive_byes,
          }
          break

        case 'home_away_balance':
          constraintData.parameters = {
            target_home_percentage: values.target_home_percentage || 50,
          }
          break

        case 'preferred_day':
          constraintData.parameters = {
            days: values.preferred_days || [],
          }
          break

        case 'restricted_day':
          constraintData.parameters = {
            days: values.restricted_days || [],
          }
          break

        case 'max_consecutive_late_games':
          constraintData.parameters = {
            late_time_threshold: values.late_time_threshold?.format('HH:mm'),
            max_consecutive: values.max_consecutive,
          }
          break

        case 'preferred_time':
          constraintData.parameters = {
            start_time: values.pref_start_time?.format('HH:mm'),
            end_time: values.pref_end_time?.format('HH:mm'),
          }
          break

        case 'max_games_per_team_per_day':
          constraintData.parameters = {
            max_games: values.max_games,
          }
          break

        case 'games_per_team_per_week':
          constraintData.parameters = {
            games_per_week: values.games_per_week,
          }
          break

        case 'facility_distribution':
          // `targets` is the ONLY parameter the API accepts — the backend
          // rejects the retired spread/tolerance keys outright.
          constraintData.parameters = {
            targets: facilityPercentageRowsToTargets(
              values.targets as FacilityPercentageTargetRow[] | undefined
            ),
          }
          break
      }

      // Duplicate check — prevent same type + same scope (skip for clones)
      if (!isEditMode && !isCloneMode && existingConstraints.length > 0) {
        const normalizeScope = (uids: string[] | null | undefined) =>
          uids && uids.length > 0 ? [...uids].sort().join(',') : ''

        const newDivScope = normalizeScope(constraintData.division_uids)
        const newTeamScope = normalizeScope(constraintData.team_uids)

        const duplicate = existingConstraints.find((c) => {
          // Same type + same scope collides, facility_distribution
          // included: with only one param family left there is no longer a
          // second row that could legitimately share a scope.
          return (
            c.type.code === effectiveTypeCode &&
            normalizeScope(c.division_uids) === newDivScope &&
            normalizeScope(c.team_uids) === newTeamScope
          )
        })

        if (duplicate) {
          message.warning(
            'A constraint of this type with the same scope already exists. ' +
              'Change the divisions or teams to create a scoped variant.'
          )
          setSubmitting(false)
          return
        }
      }

      if (isEditMode && editingConstraint && onUpdate) {
        const hasParams =
          constraintData.parameters &&
          Object.values(constraintData.parameters).some(
            (v) => v !== undefined && v !== null
          )
        const finalParameters = hasParams
          ? constraintData.parameters
          : (editingConstraint.parameters as Record<string, unknown>)

        await onUpdate(editingConstraint.id, {
          parameters: finalParameters,
          description: constraintData.description,
          is_hard: constraintData.is_hard,
          is_enabled: constraintData.is_enabled,
          division_uids: constraintData.division_uids,
          team_uids: constraintData.team_uids,
        })

        message.success('Constraint updated successfully')
      } else {
        await onSubmit(constraintData)
        message.success(
          isCloneMode
            ? 'Constraint cloned successfully'
            : 'Constraint added successfully'
        )
      }
      onClose()
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message)
      } else {
        message.error(
          isEditMode
            ? 'Failed to update constraint'
            : 'Failed to add constraint'
        )
      }
    } finally {
      setSubmitting(false)
    }
  }, [
    form,
    onSubmit,
    onUpdate,
    onClose,
    isEditMode,
    isCloneMode,
    editingConstraint,
    existingConstraints,
    selectedTypeCode,
    selectedType,
  ])

  // facility_distribution: the percentage-targets editor, one row per
  // target ([Location/Surface] [entity] [percent]). This is the WHOLE
  // constraint now — the old "Distribution Type" radio and its even-spread
  // branch are gone (even spread is the silent per-team default nobody
  // configures), as is the tolerance slack (a percentage is enforced to its
  // exact rounding band).
  const renderFacilityTargetsFields = () => (
    <>
      <div css={fieldContainerStyles}>
        <span css={fieldLabelStyles}>
          Percentage Targets
          <RequiredMark />
        </span>
        <Form.List
          name='targets'
          // One blank row on open: with no mode selector above it, an empty
          // list would paint the whole constraint as a lone dashed button.
          // Edit mode's setFieldsValue still wins over this.
          initialValue={[{ level: 'facility' }]}
          rules={[
            {
              validator: async (_, rows) => {
                const error = validateFacilityPercentageRows(
                  rows as FacilityPercentageTargetRow[] | undefined
                )
                if (error) {
                  throw new Error(error)
                }
              },
            },
          ]}
        >
          {(fields, { add, remove }, { errors }) => (
            <>
              {fields.map((field) => (
                <div key={field.key} css={percentageTargetRowStyles}>
                  <Form.Item
                    name={[field.name, 'level']}
                    initialValue='facility'
                    css={percentageLevelItemStyles}
                  >
                    <Select
                      popupClassName='dark-select-popup'
                      options={[
                        { value: 'venue', label: 'Location' },
                        { value: 'facility', label: 'Surface' },
                      ]}
                      onChange={() =>
                        form.setFieldValue(
                          ['targets', field.name, 'entity_uid'],
                          undefined
                        )
                      }
                    />
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate>
                    {() => {
                      const level = form.getFieldValue([
                        'targets',
                        field.name,
                        'level',
                      ]) as string
                      const options = level === 'venue' ? venues : facilities
                      return (
                        <Form.Item
                          name={[field.name, 'entity_uid']}
                          rules={[
                            {
                              required: true,
                              message:
                                level === 'venue'
                                  ? 'Location is required'
                                  : 'Surface is required',
                            },
                          ]}
                          css={percentageEntityItemStyles}
                        >
                          <Select
                            placeholder={
                              level === 'venue'
                                ? 'Select location'
                                : 'Select surface'
                            }
                            showSearch
                            optionFilterProp='children'
                            popupClassName='dark-select-popup'
                          >
                            {options.map((o) => (
                              <Option key={o.uid} value={o.uid}>
                                {o.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      )
                    }}
                  </Form.Item>
                  <Form.Item
                    name={[field.name, 'percent']}
                    rules={[{ required: true, message: 'Percent is required' }]}
                    css={percentagePercentItemStyles}
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      precision={0}
                      step={1}
                      addonAfter='%'
                    />
                  </Form.Item>
                  {fields.length > 1 && (
                    <button
                      type='button'
                      css={percentageRemoveButtonStyles}
                      aria-label='Remove target'
                      onClick={() => remove(field.name)}
                    >
                      <CloseOutlined />
                    </button>
                  )}
                </div>
              ))}
              <button
                type='button'
                css={percentageAddButtonStyles}
                onClick={() => add({ level: 'facility' })}
              >
                + Add Target
              </button>
              <Form.ErrorList errors={errors} />
            </>
          )}
        </Form.List>
      </div>
    </>
  )

  const renderParameterFields = () => {
    if (!selectedTypeCode) {
      return null
    }

    switch (selectedTypeCode) {
      case 'venue_blackout':
        return (
          <div css={paramGroupStyles}>
            <div css={fieldContainerStyles}>
              <span css={fieldLabelStyles}>Venue (optional)</span>
              <Form.Item name='venue_uids' noStyle>
                <Select
                  ref={venuesSelectRef}
                  mode='multiple'
                  placeholder='All venues'
                  allowClear
                  showSearch
                  optionFilterProp='children'
                  popupClassName='dark-select-popup'
                  // eslint-disable-next-line react/no-unstable-nested-components
                  dropdownRender={(menu) => (
                    <>
                      <div
                        css={selectAllItemStyles}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleSelectAllVenues}
                      >
                        All Venues
                      </div>
                      {menu}
                    </>
                  )}
                >
                  {venues.map((v) => (
                    <Option key={v.uid} value={v.uid}>
                      {v.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <div css={fieldContainerStyles}>
              <span css={fieldLabelStyles}>Facility (optional)</span>
              <Form.Item name='facility_uids' noStyle>
                <Select
                  ref={facilitiesSelectRef}
                  mode='multiple'
                  placeholder='All facilities'
                  allowClear
                  showSearch
                  optionFilterProp='children'
                  popupClassName='dark-select-popup'
                  // eslint-disable-next-line react/no-unstable-nested-components
                  dropdownRender={(menu) => (
                    <>
                      <div
                        css={selectAllItemStyles}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleSelectAllFacilities}
                      >
                        All Facilities
                      </div>
                      {menu}
                    </>
                  )}
                >
                  {facilities.map((f) => (
                    <Option key={f.uid} value={f.uid}>
                      {f.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <div css={fieldContainerStyles}>
              <span css={fieldLabelStyles}>
                Blackout Dates
                <RequiredMark />
              </span>
              <Form.Item
                name='dates'
                rules={[
                  { required: true, message: 'At least one date is required' },
                ]}
              >
                <DatePicker.RangePicker
                  style={fullWidthStyle}
                  format='YYYY-MM-DD'
                />
              </Form.Item>
            </div>
          </div>
        )

      case 'venue_capacity':
        return (
          <div css={paramGroupStyles}>
            <div css={fieldContainerStyles}>
              <span css={fieldLabelStyles}>
                Facilities
                <RequiredMark />
              </span>
              <Form.Item
                name='facility_uids'
                rules={[
                  {
                    required: true,
                    message: 'At least one facility is required',
                  },
                ]}
              >
                <Select
                  ref={facilitiesSelectRef}
                  mode='multiple'
                  placeholder='Select facilities'
                  allowClear
                  showSearch
                  optionFilterProp='children'
                  popupClassName='dark-select-popup'
                  // eslint-disable-next-line react/no-unstable-nested-components
                  dropdownRender={(menu) => (
                    <>
                      <div
                        css={selectAllItemStyles}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleSelectAllFacilities}
                      >
                        All Facilities
                      </div>
                      {menu}
                    </>
                  )}
                >
                  {facilities.map((f) => (
                    <Option key={f.uid} value={f.uid}>
                      {f.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <div css={fieldContainerStyles}>
              <span css={fieldLabelStyles}>
                Max Concurrent Games
                <RequiredMark />
              </span>
              <Form.Item
                name='max_concurrent_games'
                rules={[{ required: true, message: 'Max games is required' }]}
              >
                <InputNumber min={1} max={20} style={fullWidthStyle} />
              </Form.Item>
            </div>
          </div>
        )

      case 'time_window':
        return (
          <div css={paramGroupStyles}>
            <div
              css={css`
                display: flex;
                align-items: flex-end;
                gap: 8px;
              `}
            >
              {/* eslint-disable-next-line react-native/no-inline-styles */}
              <div css={fieldContainerStyles} style={{ flex: 1 }}>
                <span css={fieldLabelStyles}>
                  Start Time
                  <RequiredMark />
                </span>
                <Form.Item
                  name='start_time'
                  rules={[
                    { required: true, message: 'Start time is required' },
                  ]}
                >
                  <TimePickerWithInterval style={fullWidthStyle} />
                </Form.Item>
              </div>
              {/* eslint-disable-next-line react-native/no-inline-styles */}
              <div css={fieldContainerStyles} style={{ flex: 1 }}>
                <span css={fieldLabelStyles}>
                  End Time
                  <RequiredMark />
                </span>
                <Form.Item
                  name='end_time'
                  rules={[{ required: true, message: 'End time is required' }]}
                >
                  <TimePickerWithInterval
                    style={fullWidthStyle}
                    relativeToTime={watchedStartTime}
                  />
                </Form.Item>
              </div>
            </div>
            <div css={fieldContainerStyles}>
              <span css={fieldLabelStyles}>Days (optional)</span>
              <Form.Item name='days'>
                <DayPicker />
              </Form.Item>
            </div>
          </div>
        )

      case 'team_bye_period':
        return (
          <div css={paramGroupStyles}>
            <div css={fieldContainerStyles}>
              <span css={fieldLabelStyles}>
                Bye Period
                <RequiredMark />
              </span>
              <Form.Item
                name='bye_date_range'
                rules={[{ required: true, message: 'Date range is required' }]}
              >
                <DatePicker.RangePicker
                  style={fullWidthStyle}
                  format='YYYY-MM-DD'
                />
              </Form.Item>
            </div>
          </div>
        )

      case 'no_back_to_back':
        return (
          <div css={fieldContainerStyles}>
            <span css={fieldLabelStyles}>
              Minimum Time Between Games (minutes)
              <RequiredMark />
            </span>
            <Form.Item
              name='min_minutes_between'
              rules={[{ required: true, message: 'Minimum time is required' }]}
              initialValue={60}
            >
              <InputNumber
                min={15}
                max={10080}
                step={15}
                style={fullWidthStyle}
                addonAfter='min'
              />
            </Form.Item>
          </div>
        )

      case 'min_rest_days':
        return (
          <div css={fieldContainerStyles}>
            <span css={fieldLabelStyles}>
              Minimum Rest Days
              <RequiredMark />
            </span>
            <Form.Item
              name='min_days'
              rules={[{ required: true, message: 'Min days is required' }]}
              initialValue={1}
            >
              <InputNumber min={1} max={14} style={fullWidthStyle} />
            </Form.Item>
          </div>
        )

      case 'bye_distribution':
        return (
          <div css={fieldContainerStyles}>
            <span css={fieldLabelStyles}>
              Max Consecutive Byes
              <RequiredMark />
            </span>
            <Form.Item
              name='max_consecutive_byes'
              rules={[{ required: true, message: 'Max byes is required' }]}
              initialValue={2}
            >
              <InputNumber min={1} max={10} style={fullWidthStyle} />
            </Form.Item>
          </div>
        )

      case 'facility_distribution':
        return <div css={paramGroupStyles}>{renderFacilityTargetsFields()}</div>

      case 'home_away_balance':
        return (
          <div css={fieldContainerStyles}>
            <span css={fieldLabelStyles}>Target Home Percentage</span>
            <Form.Item name='target_home_percentage' initialValue={50}>
              <InputNumber
                min={0}
                max={100}
                style={fullWidthStyle}
                addonAfter='%'
              />
            </Form.Item>
          </div>
        )

      case 'preferred_day':
        return (
          <div css={fieldContainerStyles}>
            <span css={fieldLabelStyles}>
              Preferred Days
              <RequiredMark />
            </span>
            <Form.Item
              name='preferred_days'
              rules={[
                { required: true, message: 'At least one day is required' },
              ]}
            >
              <DayPicker />
            </Form.Item>
          </div>
        )

      case 'preferred_time':
        return (
          <div css={paramGroupStyles}>
            <div
              css={css`
                display: flex;
                align-items: flex-end;
                gap: 8px;
              `}
            >
              {/* eslint-disable-next-line react-native/no-inline-styles */}
              <div css={fieldContainerStyles} style={{ flex: 1 }}>
                <span css={fieldLabelStyles}>
                  Start Time
                  <RequiredMark />
                </span>
                <Form.Item
                  name='pref_start_time'
                  rules={[
                    { required: true, message: 'Start time is required' },
                  ]}
                >
                  <TimePickerWithInterval style={fullWidthStyle} />
                </Form.Item>
              </div>
              {/* eslint-disable-next-line react-native/no-inline-styles */}
              <div css={fieldContainerStyles} style={{ flex: 1 }}>
                <span css={fieldLabelStyles}>
                  End Time
                  <RequiredMark />
                </span>
                <Form.Item
                  name='pref_end_time'
                  rules={[{ required: true, message: 'End time is required' }]}
                >
                  <TimePickerWithInterval
                    style={fullWidthStyle}
                    relativeToTime={watchedPrefStartTime}
                  />
                </Form.Item>
              </div>
            </div>
          </div>
        )

      case 'max_games_per_team_per_day':
        return (
          <div css={fieldContainerStyles}>
            <span css={fieldLabelStyles}>
              Max Games Per Team Per Day
              <RequiredMark />
            </span>
            <Form.Item
              name='max_games'
              rules={[
                {
                  required: true,
                  message: 'Max games per team per day is required',
                },
              ]}
              initialValue={2}
            >
              <InputNumber min={1} max={10} style={fullWidthStyle} />
            </Form.Item>
          </div>
        )

      case 'games_per_team_per_week':
        return (
          <div css={fieldContainerStyles}>
            <span css={fieldLabelStyles}>
              Games Per Week
              <RequiredMark />
            </span>
            <Form.Item
              name='games_per_week'
              rules={[
                {
                  required: true,
                  message: 'Games per week is required',
                },
              ]}
              initialValue={1}
            >
              <InputNumber min={1} max={7} style={fullWidthStyle} />
            </Form.Item>
          </div>
        )

      case 'restricted_day':
        return (
          <div css={fieldContainerStyles}>
            <span css={fieldLabelStyles}>
              Restricted Days
              <RequiredMark />
            </span>
            <Form.Item
              name='restricted_days'
              rules={[
                { required: true, message: 'At least one day is required' },
              ]}
            >
              <DayPicker />
            </Form.Item>
          </div>
        )

      case 'max_consecutive_late_games':
        return (
          <div css={paramGroupStyles}>
            <div css={fieldContainerStyles}>
              <span css={fieldLabelStyles}>
                Late Game Threshold
                <RequiredMark />
              </span>
              <Form.Item
                name='late_time_threshold'
                rules={[
                  { required: true, message: 'Threshold time is required' },
                ]}
              >
                <TimePickerWithInterval style={fullWidthStyle} />
              </Form.Item>
            </div>
            <div css={fieldContainerStyles}>
              <span css={fieldLabelStyles}>
                Max Consecutive Late Games
                <RequiredMark />
              </span>
              <Form.Item
                name='max_consecutive'
                rules={[
                  {
                    required: true,
                    message: 'Max consecutive is required',
                  },
                ]}
                initialValue={2}
              >
                <InputNumber min={1} max={10} style={fullWidthStyle} />
              </Form.Item>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <>
      <Global styles={darkPortalStyles} />
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        closable={false}
        width={600}
        css={modalStyles}
        styles={{
          body: { padding: 0 },
          content: { padding: 0 },
        }}
      >
        {/* ---- Header ---- */}
        <div css={headerStyles}>
          <div css={headerContentStyles}>
            <h2 css={headerTitleStyles}>
              {isEditMode
                ? `Edit Constraint - ${editingConstraint?.type?.name || ''}`
                : isCloneMode
                ? 'Clone Constraint'
                : 'Add Constraint'}
            </h2>
            <button type='button' css={closeButtonStyles} onClick={onClose}>
              <CloseOutlined />
            </button>
          </div>
        </div>

        {/* ---- Form Body ---- */}
        <div css={formBodyStyles}>
          <Form
            form={form}
            layout='vertical'
            initialValues={{ is_hard: false, is_enabled: true }}
          >
            {/* Type + Active Row */}
            <div css={typeActiveRowStyles}>
              {/* Type Section */}
              <div css={typeActiveSectionStyles}>
                <div css={typeActiveInfoStyles}>
                  <span css={typeActiveLabelStyles}>Type</span>
                  <span css={typeActiveDescStyles}>
                    How the constraint affect schedule
                  </span>
                </div>
                <Form.Item name='is_hard' noStyle>
                  <HardSoftToggle
                    lockedHard={selectedTypeCode === 'venue_capacity'}
                  />
                </Form.Item>
              </div>

              {/* Vertical Divider */}
              <div css={verticalDividerStyles} />

              {/* Active Section */}
              <div css={typeActiveSectionStyles}>
                <div css={typeActiveInfoStyles}>
                  <span css={typeActiveLabelStyles}>Active</span>
                  <span css={typeActiveDescStyles}>
                    The constraint affects the schedule
                  </span>
                </div>
                <Form.Item name='is_enabled' valuePropName='checked' noStyle>
                  <Switch />
                </Form.Item>
              </div>
            </div>

            {/* Category + Constraint (Add mode only) */}
            {!isEditMode && (
              <>
                <div css={fieldContainerStyles}>
                  <span css={fieldLabelStyles}>
                    Category
                    <RequiredMark />
                  </span>
                  <Select
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    placeholder='Select category'
                    popupClassName='dark-select-popup'
                    loading={typesLoading}
                    disabled={!!preSelectedTypeCode}
                  >
                    {categories.map((c) => (
                      <Option key={c.value} value={c.value}>
                        {c.label}
                      </Option>
                    ))}
                  </Select>
                </div>

                <div css={fieldContainerStyles}>
                  <span css={fieldLabelStyles}>
                    Constraint
                    <RequiredMark />
                  </span>
                  <Form.Item
                    name='constraint_type_code'
                    rules={[
                      {
                        required: true,
                        message: 'Please select a constraint type',
                      },
                    ]}
                  >
                    <Select
                      placeholder='Select constraint'
                      disabled={!selectedCategory || !!preSelectedTypeCode}
                      loading={typesLoading}
                      onChange={handleTypeChange}
                      showSearch
                      optionFilterProp='children'
                      popupClassName='dark-select-popup'
                    >
                      {filteredConstraintTypes.map((type) => (
                        <Option key={type.code} value={type.code}>
                          {getConstraintDisplayName(type.code, type.name)}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </>
            )}

            {/* Description */}
            <div css={fieldContainerStyles}>
              <span css={fieldLabelStyles}>Description (optional)</span>
              <Form.Item name='description' noStyle>
                <TextArea
                  rows={3}
                  placeholder='Add a description for this constraint'
                />
              </Form.Item>
            </div>

            {/* Parameter fields */}
            {selectedTypeCode && (
              <div css={paramGroupStyles}>{renderParameterFields()}</div>
            )}

            {/* Scope selectors — always visible for non-venue constraints */}
            {selectedTypeCode &&
              selectedTypeCode !== 'venue_capacity' &&
              selectedTypeCode !== 'venue_blackout' && (
                <>
                  <div css={fieldContainerStyles}>
                    <span css={fieldLabelStyles}>Divisions</span>
                    <Form.Item name='division_uids'>
                      <Select
                        ref={divisionsSelectRef}
                        mode='multiple'
                        placeholder='All Divisions'
                        allowClear
                        showSearch
                        optionFilterProp='children'
                        popupClassName='dark-select-popup'
                        // eslint-disable-next-line react/no-unstable-nested-components
                        dropdownRender={(menu) => (
                          <>
                            <div
                              css={selectAllItemStyles}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={handleSelectAllDivisions}
                            >
                              All Divisions
                            </div>
                            {menu}
                          </>
                        )}
                      >
                        {divisions.map((d) => (
                          <Option key={d.uid} value={d.uid}>
                            {d.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>

                  <div css={fieldContainerStyles}>
                    <span css={fieldLabelStyles}>Teams</span>
                    <Form.Item name='team_uids'>
                      <Select
                        ref={teamsSelectRef}
                        mode='multiple'
                        placeholder='All Teams'
                        allowClear
                        showSearch
                        optionFilterProp='children'
                        popupClassName='dark-select-popup'
                        // eslint-disable-next-line react/no-unstable-nested-components
                        dropdownRender={(menu) => (
                          <>
                            <div
                              css={selectAllItemStyles}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={handleSelectAllTeams}
                            >
                              All Teams
                            </div>
                            {menu}
                          </>
                        )}
                      >
                        {teamsByDivision.map((group) => (
                          <OptGroup
                            key={group.division.uid}
                            label={group.division.name}
                          >
                            {group.teams.map((t) => (
                              <Option key={t.uid} value={t.uid}>
                                {t.name}
                              </Option>
                            ))}
                          </OptGroup>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>
                </>
              )}
          </Form>
        </div>

        {/* ---- Footer ---- */}
        <div css={footerStyles}>
          <div css={footerButtonsStyles}>
            <button type='button' css={cancelButtonStyles} onClick={onClose}>
              Cancel
            </button>
            <button
              type='button'
              css={confirmButtonStyles}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {isEditMode ? 'Save Changes' : isCloneMode ? 'Clone' : 'Confirm'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default AddConstraintModal
